import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { PageSEO } from "@/components/PageSEO";
import { CalendarCheck, Check } from "lucide-react";
import { API } from "@/lib/constants";
import { QmdpAttivazioneForm } from "@/components/QmdpAttivazioneForm";

/* Pagina di vendita di «Quanto Mettere da Parte — Forfettari».
   Il pulsante si attiva solo se l'interruttore «Vendita attiva» è acceso in /admin.
   Con ?prova=1 si apre il modulo in modalità di prova Stripe (per le verifiche interne). */

const PUNTI = [
  "Sai ogni mese quanto mettere da parte",
  "Conosci in anticipo date e importi dei pagamenti",
  "Niente sorprese a giugno e novembre",
];

const PASSI = [
  ["1", "Compili una volta la scheda con i dati della tua attività: settore, aliquota, contributi, pagamenti già fatti."],
  ["2", "Il 1° di ogni mese ricevi una mail con una sola domanda: quanto hai incassato il mese scorso?"],
  ["3", "Ricevi subito quanto mettere da parte e il calendario dei prossimi pagamenti con gli importi."],
];

const SCADENZE = [
  ["16/11/2026 · contributi INPS (rata fissa)", "1.153 €"],
  ["30/11/2026 · secondo acconto imposta", "550 €"],
  ["16/02/2027 · contributi INPS (rata fissa)", "1.153 €"],
];

const CONFRONTO = [
  {
    testid: "gestore-forfettario",
    nome: "Gestore Forfettario Pro",
    etichetta: "FILE EXCEL",
    prezzo: "39,97 € una volta",
    link: "/strumenti/gestore-forfettario",
    azione: "VEDI LO STRUMENTO",
    righe: [
      ["COSA FAI TU", "Inserisci nel foglio incassi e dati e tieni aggiornati i calcoli"],
      ["COSA RICEVI", "Un foglio di calcolo da usare quando vuoi"],
      ["DICHIARAZIONE E F24", "Li gestisci tu o il tuo commercialista"],
      ["È PER CHI", "Vuole fare i conti da sé su Excel"],
    ],
  },
  {
    testid: "quanto-mettere-da-parte",
    nome: "Quanto Mettere da Parte",
    etichetta: "SERVIZIO MENSILE",
    prezzo: "9,90 € al mese",
    link: "/strumenti/quanto-mettere-da-parte",
    azione: "SEI QUI",
    evidenziato: true,
    righe: [
      ["COSA FAI TU", "Un solo dato al mese: quanto hai incassato"],
      ["COSA RICEVI", "Ogni mese un messaggio con quanto mettere da parte, quanto ti resta dell'incasso e i prossimi pagamenti con date e importi"],
      ["DICHIARAZIONE E F24", "Li gestisci tu o il tuo commercialista"],
      ["È PER CHI", "Gestisce già gli adempimenti ma vuole sapere ogni mese quanto tenere da parte, con un dato solo"],
    ],
  },
  {
    testid: "contabilita-forfettari",
    nome: "Contabilità forfettari",
    etichetta: "CE NE OCCUPIAMO NOI",
    prezzo: "43 € al mese",
    link: "/forfettari",
    azione: "VEDI L'ABBONAMENTO",
    righe: [
      ["COSA FAI TU", "Ci mandi i documenti che ti chiediamo"],
      ["COSA RICEVI", "Dichiarazione dei redditi, imposta, contributi INPS e F24 preparati da noi"],
      ["DICHIARAZIONE E F24", "Li prepariamo noi"],
      ["È PER CHI", "Vuole che tutto sia seguito dallo studio"],
    ],
  },
];

const Anteprima = ({ titolo, children, testid }) => (
  <div className="bg-white border border-[#E8E2D5] rounded-lg overflow-hidden" data-testid={testid}>
    <div className="bg-[#0D1527] px-5 py-2.5">
      <span className="text-[#7CC0EE] text-[10px] font-bold tracking-[0.2em]">{titolo}</span>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const QmdpVenditaPage = () => {
  const [params] = useSearchParams();
  const prova = params.get("prova") === "1";
  const [venditaAttiva, setVenditaAttiva] = useState(false);
  const [apri, setApri] = useState(false);

  useEffect(() => {
    axios.get(`${API}/qmdp-stato`)
      .then(({ data }) => setVenditaAttiva(!!data.vendita_attiva))
      .catch(() => setVenditaAttiva(false));
  }, []);

  const VENDITA_ATTIVA = venditaAttiva || prova;

  return (
  <main className="pt-28 pb-24 min-h-screen bg-[#FAF8F5]" data-testid="qmdp-vendita-page">
    <PageSEO
      title="Quanto Mettere da Parte — Forfettari | SC Dottori Commercialisti"
      description="Ogni mese ci dici quanto hai incassato, noi ti diciamo quanto tenere da parte per tasse e contributi e quando dovrai pagarli. 9,90 € al mese, IVA inclusa."
      noindex={!venditaAttiva}
    />
    <div className="max-w-6xl mx-auto px-6 lg:px-10">
      {/* Hero */}
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-12 items-start">
        <div>
          <p className="text-[#2F7DBF] text-xs tracking-[0.3em] mb-5">QUANTO METTERE DA PARTE — FORFETTARI</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#0D1527] leading-[1.05]">
            QUANDO ARRIVA L'F24, I SOLDI LI HAI <span className="text-[#2F7DBF]">GIÀ MESSI DA PARTE</span>
          </h1>
          <p className="text-[#4A5568] text-base md:text-lg mt-6 max-w-xl">
            Ogni mese ci dici quanto hai incassato, noi ti diciamo quanto tenere da parte per tasse e contributi e quando
            dovrai pagarli.
          </p>
          <ul className="mt-8 space-y-3">
            {PUNTI.map((p) => (
              <li key={p} className="flex items-start gap-3 text-[#3B4658]">
                <Check className="text-[#2F7DBF] mt-0.5 shrink-0" size={18} /> {p}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white border-2 border-[#3E8ED0] rounded-lg p-7 lg:sticky lg:top-28" data-testid="qmdp-box-acquisto">
          <div className="flex items-center gap-2 mb-4">
            <CalendarCheck className="text-[#2F7DBF]" size={20} />
            <span className="text-[#64708A] text-[11px] font-bold tracking-[0.2em]">ABBONAMENTO MENSILE</span>
          </div>
          <p className="font-heading text-4xl text-[#2F7DBF]">9,90 € <span className="text-base text-[#64708A]">al mese IVA inclusa</span></p>
          <button
            disabled={!VENDITA_ATTIVA}
            onClick={() => setApri(true)}
            data-testid="qmdp-attiva"
            className="w-full mt-6 px-6 py-3.5 rounded bg-[#2F7DBF] text-white font-bold tracking-wide hover:bg-[#1F4E75] transition-colors disabled:opacity-40 disabled:hover:bg-[#2F7DBF]"
          >
            ATTIVA ORA
          </button>
          {!VENDITA_ATTIVA && (
            <p className="text-[#8E9AAE] text-xs mt-3" data-testid="qmdp-non-in-vendita">
              Le attivazioni non sono ancora aperte. Scrivici a assistenza@casalisilvia.com per essere avvisato.
            </p>
          )}
          {apri && VENDITA_ATTIVA && <QmdpAttivazioneForm prova={prova} onChiudi={() => setApri(false)} />}
          <p className="text-[#64708A] text-xs mt-4">
            Disdici quando vuoi dal link «Disdici l'abbonamento» presente in ogni email.{" "}
            <Link className="text-[#2F7DBF] underline" to="/strumenti/quanto-mettere-da-parte/condizioni">Condizioni generali</Link>.
          </p>
        </div>
      </div>

      {/* Come funziona */}
      <h2 className="font-heading text-3xl sm:text-4xl text-[#0D1527] mt-24">COME <span className="text-[#2F7DBF]">FUNZIONA</span></h2>
      <div className="grid md:grid-cols-3 gap-6 mt-8">
        {PASSI.map(([n, t]) => (
          <div key={n} className="bg-white border border-[#E8E2D5] rounded-lg p-6">
            <span className="font-heading text-3xl text-[#3E8ED0]">{n}</span>
            <p className="text-[#4A5568] text-sm mt-3">{t}</p>
          </div>
        ))}
      </div>

      {/* Tre anteprime */}
      <h2 className="font-heading text-3xl sm:text-4xl text-[#0D1527] mt-24">COSA <span className="text-[#2F7DBF]">RICEVI</span></h2>
      <p className="text-[#8E9AAE] text-sm mt-3">Esempio con dati inventati: commerciante, negozio, aliquota 15%, contributi fissi INPS.</p>
      <div className="grid md:grid-cols-3 gap-6 mt-8">
        <Anteprima titolo="1 · LA TUA SCHEDA, UNA VOLTA SOLA" testid="qmdp-anteprima-scheda">
          {[["Che attività svolgi?", "Commercio al dettaglio"],
            ["Anno di inizio attività", "2019"],
            ["Aliquota dell'imposta", "15%"],
            ["Dove versi i contributi", "INPS Commercianti"],
            ["Incassato da gennaio a settembre", "22.500 €"]].map(([k, v]) => (
            <div key={k} className="mb-3">
              <span className="block text-[#8E9AAE] text-[11px]">{k}</span>
              <span className="block border border-[#E8E2D5] rounded px-3 py-2 text-[#0D1527] text-sm mt-1">{v}</span>
            </div>
          ))}
        </Anteprima>

        <Anteprima titolo="2 · LA DOMANDA DEL 1° DEL MESE" testid="qmdp-anteprima-domanda">
          <p className="text-[#8E9AAE] text-[11px]">Oggetto: <b className="text-[#3B4658]">Quanto hai incassato a ottobre?</b></p>
          <p className="text-[#4A5568] text-sm mt-3">Ciao Marco, dimmi quanto hai incassato a ottobre e ti dico subito quanto mettere da parte.</p>
          <div className="mt-4">
            <span className="block text-[#8E9AAE] text-[11px]">Incassato a ottobre</span>
            <span className="block border border-[#E8E2D5] rounded px-3 py-2 text-[#0D1527] text-sm mt-1">2.300 €</span>
          </div>
          <span className="inline-block mt-4 px-4 py-2 rounded bg-[#2F7DBF] text-white text-xs font-bold tracking-wide">CALCOLA</span>
          <p className="text-[#8E9AAE] text-[11px] mt-3">Ci vuole un minuto. Se non hai incassato nulla, scrivi 0.</p>
        </Anteprima>

        <Anteprima titolo="3 · IL MESSAGGIO DEL MESE" testid="qmdp-anteprima-messaggio">
          <div className="bg-[#F3F9FD] border border-[#BEDDF5] rounded p-4 text-center">
            <span className="block text-[#64708A] text-[10px] tracking-[0.2em]">QUESTO MESE METTI DA PARTE</span>
            <span className="block font-heading text-3xl text-[#2F7DBF] my-1">441 €</span>
            <span className="block text-[#64708A] text-[11px]">su 100 € incassati, 19 € servono per tasse e contributi</span>
          </div>
          <p className="text-[#0D1527] text-sm mt-3">Del tuo incasso di ottobre ti restano <b>1.859 €</b>.</p>
          <p className="text-[#4A5568] text-sm mt-4">Da inizio anno hai incassato <b className="text-[#0D1527]">24.800 €</b> e dovresti avere da parte <b className="text-[#0D1527]">1.806 €</b>.</p>
          <p className="text-[#3B4658] text-xs font-bold mt-4 mb-1">Prossimi pagamenti</p>
          <table className="w-full text-xs">
            <tbody>
              {SCADENZE.map(([k, v]) => (
                <tr key={k} className="border-b border-[#E8E2D5]">
                  <td className="py-1.5 text-[#64708A]">{k}</td>
                  <td className="py-1.5 text-right text-[#2F7DBF] font-mono whitespace-nowrap">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Anteprima>
      </div>

      {/* Quale fa per te */}
      <h2 className="font-heading text-3xl sm:text-4xl text-[#0D1527] mt-24">QUALE FA <span className="text-[#2F7DBF]">PER TE</span></h2>
      <p className="text-[#4A5568] mt-3">Tre modi di tenere sotto controllo tasse e contributi, dal più fai-da-te al più completo.</p>
      <div className="grid md:grid-cols-3 gap-6 mt-8" data-testid="qmdp-confronto">
        {CONFRONTO.map((c) => (
          <Link
            key={c.nome}
            to={c.link}
            data-testid={`qmdp-confronto-${c.testid}`}
            className={`group bg-white rounded-lg p-6 flex flex-col transition-all hover:scale-[1.02] ${
              c.evidenziato ? "border-2 border-[#3E8ED0]" : "border border-[#E8E2D5] hover:border-gold/50"
            }`}
          >
            <span className={`self-start text-[10px] font-bold tracking-[0.18em] px-2.5 py-1 rounded ${
              c.evidenziato ? "bg-[#0D1527] text-[#7CC0EE]" : "bg-[#0D1527] text-[#E8E2D5]"
            }`}>
              {c.etichetta}
            </span>
            <h3 className="font-heading text-2xl text-[#0D1527] mt-4">{c.nome}</h3>
            <p className={`font-heading text-2xl mt-1 ${c.evidenziato ? "text-[#2F7DBF]" : "text-gold"}`}>{c.prezzo}</p>
            <dl className="mt-5 space-y-3 flex-1">
              {c.righe.map(([k, v]) => (
                <div key={k}>
                  <dt className="text-[#8E9AAE] text-[11px] tracking-wide">{k}</dt>
                  <dd className="text-[#3B4658] text-sm">{v}</dd>
                </div>
              ))}
            </dl>
            <span className={`text-sm font-bold mt-6 ${c.evidenziato ? "text-[#2F7DBF]" : "text-gold"}`}>
              {c.azione} →
            </span>
          </Link>
        ))}
      </div>

      <p className="text-[#64708A] text-sm mt-14 bg-white border border-[#E8E2D5] rounded-lg p-6" data-testid="qmdp-disclaimer">
        È uno strumento di calcolo automatico per chi è nel regime forfettario: le cifre dipendono dai dati che inserisci
        e non sostituiscono la consulenza sulla tua situazione. Non sai se sei nel regime forfettario?{" "}
        <Link className="text-[#2F7DBF] underline" to="/contatti">Scrivici</Link>.
      </p>
    </div>
  </main>
  );
};

export default QmdpVenditaPage;
